// Copyright (c) Hamilton Manufacturing Corp.

import React from 'react';
import styled from 'styled-components';
import { currency, generateId, isMobile, plural } from './api/utility';
import Button from './form/Button';
import Icon from './form/Icon';
import { useGlobalState } from './globalState/GlobalStateProvider';
import Modal from './Modal';
import PaymentForm from './PaymentForm';
import Loading from './Loading';
import { QuestRoute } from './api/QuestRoutes';
import Form from './form/Form';
import Input from './form/Input';
import { Regex } from './api/regex';
import Select from './form/Select';
import { generateQR } from '../_pwa/WashClub';
import CheckBox from './form/CheckBox';
import Col from './form/Col';
import { HamQuest } from '../../App';

const SmallInt = styled.span`
    position: absolute;
    font-size: 0.7em;
    vertical-align: top;
    margin-left: 0.2em;
    top: -60%;
    right: -30%;
`;
const RedDot = styled(SmallInt)`
    top: -80%;
    right: -80%;
    background: red;
    border-radius: 50%;
    width: 1.5em;
    height: 1.5em;
    line-height: 1.5em;
    text-align: center;
    color: ${({ theme }) => theme.white};
`;
const Row = styled.div`
    display: flex;
    position: relative;
    align-items: center;
`;
const StyledForm = styled(Form)`
    width: 80vw;
    max-width: 600px;
`;

export const itemTypes = {
    recharge: 'GiftCardRecharge',
    giftCard: 'GiftCard',
    wash: 'AppWash',
    bundle: 'AppBundle',
    washClub: 'AppClub',
}

const Cart = ({ label, ...props }) => {
    const {
        state: {
            cart,
            active_user,
            site_config
        },
        setCart,
        getUser,
        getUnreadNotifications,
        addToGiftList,
        getWashClub,
        updateEmailConfirmation
    } = useGlobalState();

    const addToCart = async item => {
        const WC = cart.find(e => e.productType === 'AppClub');
        if (item.productType === 'AppClub' && WC) {
            if (await Modal.Confirmation({
                title: 'Hold On!',
                type: 'warning',
                body: `Are you sure you want to replace ${WC.name} for the ${item.name}?`,
                hideClose: true,
                closeAfter: true,
            })) {
                removeFromCart(WC)
            } else {
                return;
            }
        }

        item.id = generateId(5);

        setCart([...cart, item])
    }

    const handleRecharge = item => {
        return new Promise((resolve, reject) => {
            Modal.Show({
                title: 'Enter your Card Number',
                body: <Form
                    inputs={{
                        cardNumber: '',
                    }}
                    onSubmit={(e) => {
                        resolve(e.cardNumber)
                    }}
                    submitButton={`Reload: ${currency.format(item.price)}`}
                    localForm
                >
                    <Input
                        label='Gift Card Number'
                        name='cardNumber'
                        sanitize={Regex.match.notNumbers}
                        mask={Regex.mask.creditCard}
                        maxLength={19}
                        validate={Regex.test.creditCard}
                        required
                    />
                </Form>
            })
        })
    }

    const addFulfillableAddress = () => {
        return new Promise((resolve, reject) => {
            const form = (
                <StyledForm
                    inputs={{
                        first: '',
                        last: '',
                        address1: '',
                        address2: '',
                        city: '',
                        zipCode: '',
                        state: '',
                        useHome: false,
                        isHome: false,
                    }}
                    onSubmit={(e) => {
                        Modal.Close();
                        e.addressedTo = `${e.first} ${e.last}`
                        if (e.useHome) getUser()
                        resolve(e);
                    }}
                    submitButton='Add Shipping Address'
                    localForm
                >
                    <Input
                        label='First Name'
                        name='first'
                        type='text'
                        required
                    />
                    <Input
                        label='Last Name'
                        name='last'
                        type='text'
                        required
                    />
                    <Input
                        label='Address'
                        name='address1'
                        type='text'
                        required
                        disabledcondition='useHome'
                    />
                    <Input
                        label='Address 2'
                        name='address2'
                        type='text'
                        disabledcondition='useHome'
                    />
                    <Input
                        label='City'
                        name='city'
                        type='text'
                        required
                        disabledcondition='useHome'
                    />
                    <Input
                        label='Zip Code'
                        name='zipCode'
                        type='text'
                        required
                        disabledcondition='useHome'
                    />
                    <Select
                        label='State'
                        name='state'
                        disabledcondition='useHome'
                        required
                        options={
                            [
                                {
                                    value: '',
                                    label: '-'
                                },
                                ...site_config.states
                            ]
                        }
                    />
                    <CheckBox
                        label='Save As Home Address?'
                        name='isHome'
                        disabledcondition='useHome'
                    />
                    {
                        // active_user.address &&
                        <CheckBox
                            label='Use Home Address?'
                            name='useHome'
                        />
                    }

                </StyledForm>
            )

            Modal.Show({
                title: 'Shipping To!',
                body: form,
                icon: 'shipping'
            })
        })
    }

    const addGiftToCart = item => {
        const form = (
            <StyledForm
                inputs={{
                    pastGifts: null,
                    giftedEmail: '',
                    giftMessage: '',
                    giftedTo: '',
                    giftHeader: '',
                }}
                onSubmit={(e) => {
                    return new Promise((resolve, reject) => {
                        const newGift = e.pastGifts || e.giftedEmail;
                        if (!newGift) {
                            reject({ blob: 'No Recipient selected.' });
                            return;
                        }
                        addToGiftList(newGift)
                        addToCart({ ...item, isGift: true, giftedEmail: newGift, giftMessage: e.giftMessage, giftedTo: e.giftedTo, giftHeader: e.giftHeader });
                        Modal.Close();
                    })
                }}
                submitButton='Add Gift to Cart'
            >
                {
                    active_user.pastGifts?.length ?
                        <Select
                            name='pastGifts'
                            label='Previous'
                            nullable
                            options={active_user.pastGifts.map(e => { return { label: e, value: e } })}
                        /> : null
                }
                <Input
                    label='Recipient Email'
                    name='giftedEmail'
                    type='email'
                    validate={Regex.test.email}
                    required
                    disabledcondition='pastGifts'
                />
                <Input
                    label='Recipient Name (optional)'
                    name='giftedTo'
                    type='text'
                />
                <Select
                    name='giftHeader'
                    label='Header'
                    nullable
                    options={[
                        { label: 'Happy Birthday!', value: 0 },
                        { label: 'Congratulations!', value: 1 },
                        { label: 'Good Luck!', value: 2 },
                        { label: 'A Gift For You', value: 3 },
                        { label: 'Thank You!', value: 4 },
                        { label: 'For You!', value: 5 },
                        { label: 'Happy Thanksgiving!', value: 6 },
                        { label: 'Merry Christmas!', value: 7 },
                        { label: 'Happy Holidays!', value: 8 },
                    ]}
                />
                <Input
                    label='Message (optional)'
                    name='giftMessage'
                    type='text'
                />
            </StyledForm>
        )

        Modal.Show({
            title: 'Gifting To!',
            body: form,
            icon: 'gift'
        })
    }

    const removeFromCart = (item) => {
        const index = cart.findIndex(e => e.id === item.id)
        if (index === -1) return;
        cart.splice(index, 1)
        setCart([...cart])
    }

    const removeAllFromCart = (item) => {
        setCart([...cart.filter(e => e.productId !== item.productId)])
    }

    const emptyCart = () => setCart([]);

    const addButton = (item) => {
        const total = getItemTotal(item);
        if (item.productType === itemTypes.giftCard || item.productType === itemTypes.recharge) return null;

        if (cart.find(e => e.productId === item.productId) && item.productType === 'AppClub') {
            return <Button disabled>Added To Cart {total && `(${total})`}</Button>
        } else {
            return <Button onClick={() => addToCart(item)}>Add To Cart {total && `(${total})`}</Button>
        }
    }

    const giftButton = (item) => {
        if (
            item.productType !== itemTypes.wash &&
            item.productType !== itemTypes.bundle &&
            item.productType !== itemTypes.giftCard
        ) return null;
        let num = cart.filter(e => e.productId === item.productId && e.isGift === true)?.length;
        return <Button onClick={() => addGiftToCart(item)}>Buy as a Gift {num ? `(${num})` : null}</Button>
    }

    const removeButton = (item) => {
        if (cart.find(e => e.productId === item.productId)) {
            return <Button onClick={() => removeFromCart(item)}>Remove From Cart</Button>
        }
    }

    // TODO Cart config passed into modals
    const buyNow = async (item) => {
        try {
            // HERE
            // if (!active_user.payment) {
            if (!active_user.payment.onFile) {
                await addCardToAccount(item.price)
            }

            if (item.productType === itemTypes.recharge) {
                item.cardNumber = await handleRecharge(item);
            }

            if (item.fulfillable) {
                item.address = await addFulfillableAddress();
            }

            Modal.Confirmation({
                title: 'Complete your Purchase',
                body: (
                    <Col>
                        {item.taxes ? <div>Subtotal: {currency.format(item.price)}</div> : null}
                        {item.taxes ? <div>Tax: {currency.format(item.taxes)}</div> : null}
                        <div>Total: {currency.format(item.price + (item.taxes || 0))}</div>
                    </Col>
                ),
                closeAfter: true,
            })
                .then(res => { if (res) finalize([item], false) });
        } catch (ex) {
            console.log(ex)
        }
    }

    const completePurchase = async () => {
        try {
            const amount = getCartTotal();
            // HERE 
            // if (!active_user.payment) {
            if (!active_user.payment.onFile) {
                await addCardToAccount(amount);
            }

            Modal.Confirmation({
                title: 'Complete your Purchase',
                body: <div>Total: {currency.format(amount)}</div>,
                closeAfter: true,
            })
                .then(res => { if (res) finalize(cart, true) });
        } catch (ex) {
            console.log(ex);
        }
    }

    const addCardToAccount = (amount) => {
        return new Promise(async (resolve, reject) => {
            if (!active_user.account.emailConfirmed) {
                const confirmed = await HamQuest.get(QuestRoute.user.confirmedEmail);
                if (confirmed) {
                    updateEmailConfirmation(true);
                } else {
                    Modal.Show({
                        type: 'error',
                        title: 'Please Verify your email',
                        body: 'Please verify your email before adding a card to your account.',
                        buttons: [
                            <Button onClick={() => {
                                Modal.Show({
                                    title: 'Email Verification',
                                    body: `Would you like to send a confirmation email to ${active_user.account.email}. Please verify your email to complete your account setup.`,
                                    buttons: <Button
                                        onClick={() =>
                                            Modal.Promise({
                                                title: 'Sending a new Email',
                                                callback: HamQuest.get(QuestRoute.user.confirmationEmail),
                                                body: <Loading />
                                            })
                                                .then(() => Modal.Show({
                                                    title: 'Success',
                                                    body: `An new email has been sent to ${active_user.account.email}. Please verify your email to complete your account setup.`,
                                                    type: 'success'
                                                }))
                                                .catch((er) => Modal.Show({
                                                    title: 'Error',
                                                    body: `An error occurred when sending an email to ${active_user.account.email}.`,
                                                    type: 'error'
                                                }))
                                        }
                                    >
                                        Resend Email
                                    </Button>
                                })
                            }}>Re-send Email</Button>
                        ]
                    })
                    reject();
                    return;
                }
            }
            let res = false;
            while (res !== true) {
                res = await tokenizeCard(amount);
                if (res !== true) {
                    await Modal.Confirmation({
                        title: 'An Error has occurred',
                        body: <div>{res}<br /><br />Would you like to try again?</div>,
                        type: 'error',
                        closeAfter: true,
                    })
                        .then(res => {
                            if (!res) throw new Error('Failed to tokenize Card')
                        })
                }
            }
            resolve()
        })
    }

    const tokenizeCard = (amount) => {
        return new Promise((resolve, reject) => {
            Modal.Promise({
                title: 'Building Payment Form',
                body: <Loading />,
            })
            const socket = HamQuest.webSocket(process.env.REACT_APP_WS_URL);

            socket.error = e => {
                Modal.Show({
                    title: 'Unable to Connect',
                    body: 'Unable to add a card at this time, please try again later.',
                    type: 'error',
                })
            };

            socket.ready = () => {
                Modal.Show({
                    title: 'Add a Card',
                    icon: 'creditCard',
                    body: (
                        <Col>
                            <span style={{ fontSize: '0.6em' }}>For security reasons a second form may open in a new window.</span>
                            <PaymentForm socketId={socket.id} amount={amount || 1} />
                        </Col>),
                    closeText: 'Cancel',
                    closeColor: "#FF0000"
                })
                    .finally(() => socket.close(1000))
            };

            socket.messaged = e => {
                socket.close();
                try {
                    if (e.success) {
                        // setUserPayment(e.resultData);
                        getUser();
                        Modal.Close();
                        resolve(true);
                    } else {
                        resolve(e.errorMessage || 'An Error has occurred whilst adding your card.');
                    }
                }
                catch (e) {
                    resolve({
                        errorMessage: e
                    });
                }
            };
        })
    }

    const finalize = async (cart, emptyTheCart) => {
        const payload = {
            products: cart.map(e => {
                return {
                    productId: e.productId,

                    address: e.address,

                    isGift: e.isGift ? true : false,
                    giftedTo: e.giftedTo,
                    giftedEmail: e.giftedEmail,
                    giftMessage: e.giftMessage,
                    giftHeader: e.giftHeader,
                    cardNumber: e.cardNumber,
                }
            })
        }

        if (cart.find(e => e.address)) {
            payload.address = cart.find(e => e.address).address;
        }

        if (!payload.address && cart.find(e => e.fulfillable)) {
            payload.address = await addFulfillableAddress();
        }

        Modal.Promise({
            title: 'Completing Purchase',
            body: <Loading />,
            callback: HamQuest.post(QuestRoute.purchase.finalize, payload)
        })
            .then(async res => {
                const code = res.code;

                if (res.approved && emptyTheCart) {
                    emptyCart()
                }

                let usedCode = false;
                getWashClub()

                if (code && isMobile()) {
                    usedCode = true;
                    generateQR(code)
                }

                if (!usedCode) {
                    const createMessage = (count, noun, action, additionalText = '') => {
                        return `${count} ${plural(count, noun)} ${count > 1 ? 'have' : 'has'} ${action} ${additionalText}`.trim();
                    };

                    const { items, gifts, fulfillable } = countItems(cart);

                    let addedItems = createMessage(items, 'Item', 'been Emailed to', active_user.account.email);
                    let giftedItems = createMessage(gifts, 'Item', 'been successfully Gifted');
                    let fulfillableItems = 'Your order has been sent to ' + site_config.companyName;

                    if (site_config.has_app) {
                        addedItems = createMessage(items, 'Item', 'been successfully added to your', isMobile() ? 'App Wallet' : 'Wallet');
                        giftedItems = createMessage(gifts, 'Item', 'been successfully Gifted');
                    }

                    const recharge = cart.find(e => e.productType === itemTypes.recharge);
                    if (recharge) {
                        addedItems = `Your ${site_config.cardName} has been reloaded with ${currency.format(recharge.value)} of value.`;
                    }

                    Modal.Show({
                        type: res.approved ? 'success' : 'error',
                        title: res.approved ? 'Success' : 'Failed',
                        body: (
                            <div>
                                <div>{items ? addedItems : ''}</div>
                                <div>{gifts ? giftedItems : ''}</div>
                                <div>{fulfillable ? fulfillableItems : ''}</div>
                            </div>
                        ),
                    });
                }
                getUnreadNotifications();
            })
            .catch(ex => {
                console.log('Failed to Complete Purchase')
                // Modal.Show({
                //     type: 'error',
                //     title: 'Unable to Complete purchase',
                //     body: ex.blob || 'Sorry, something went wrong. Please try again.',
                //     type: 'error'
                // })
            })
    }

    const countItems = (cart) => {
        let items = 0, gifts = 0, fulfillable = 0;

        cart.forEach(e => {
            if (!e.isGift && !e.fulfillable) items++;
            if (e.isGift) gifts++;
            if (e.fulfillable) fulfillable++;
        });

        return { items, gifts, fulfillable };
    };

    const purchaseButton = () => <Button onClick={completePurchase}>Purchase</Button>

    const getItemTotal = (item) => cart.filter(e => e.productId === item.productId).length || null;
    const getNonGiftItemTotal = (item) => cart.filter(e => e.productId === item.productId && !e.isGift).length || null;

    const getCartTotal = () => cart.reduce((a, b) => a + (b.price || 0) + (b.taxes || 0), 0);
    const getCartPrice = () => currency.format(cart.reduce((a, b) => a + (b.price || 0) + (b.taxes || 0), 0));
    const getCartSubTotal = () => currency.format(cart.reduce((a, b) => a + (b.price || 0), 0));

    const getItemPrices = (item) => currency.format(item.isGift ? cart.find(e => e.productId === item.productId).price : cart.filter(e => e.productId === item.productId && !e.isGift).reduce((a, b) => a + (b.price || 0), 0))

    const icon = () => <Row><Icon icon='cart' />{cart.length ? <RedDot>{cart.length}</RedDot> : null}</Row>

    Cart.Empty = emptyCart;
    Cart.AddCardToAccount = addCardToAccount;
    Cart.GetItemPrices = getItemPrices;
    Cart.GetTotal = getCartTotal;
    Cart.GetTotalPrice = getCartPrice;
    Cart.GetSubTotalPrice = getCartSubTotal;
    Cart.GetItemTotal = getItemTotal;
    Cart.GetNonGiftItemTotal = getNonGiftItemTotal;
    Cart.AddButton = addButton;
    Cart.GiftButton = giftButton;
    Cart.RemoveButton = removeButton;
    Cart.PurchaseButton = purchaseButton;
    Cart.CompletePurchase = completePurchase;
    Cart.BuyNow = buyNow;
    Cart.Add = addToCart;
    Cart.Remove = removeFromCart;
    Cart.RemoveAll = removeAllFromCart;
    Cart.Icon = icon;

    return (
        <Row>
            <Row>
                <Icon style={{ width: '1.5em' }} icon='cart' />
                {cart.length ? <SmallInt>{cart.length}</SmallInt> : null}
            </Row>
            {
                label ?
                    <div style={{ paddingLeft: cart.length ? '0.5em' : '' }}>Cart</div>
                    : null
            }
        </Row>
    );
}

export default Cart;
